/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

/* eslint-disable @rushstack/no-new-null */
/* eslint-disable @typescript-eslint/member-ordering */

import * as TicketAttributeConfig from './TicketAttributeConfig';
import * as pinUtils from './promise-pin-utils';
import * as firebaseUtils from './firebase-utils';
import { logger } from '@touchplan/logging';

import { PLAN_CONSTANTS } from './plan-constants';
import { Side } from './Side';
import { Role } from './Role';
import { Variance } from './Variance';
import { dateCache } from './date-cache';
import { IPreviousAssignedProjectMember } from './IPreviousAssignedProjectMember';
import { CONSTRAINT_ACCEPTANCE_STATE } from './constraint-acceptance-state';
import { ProjectMember } from './ProjectMember';
import { Location } from './Location';
import { MilestoneType, TicketType } from './TicketType';
import { Priority } from './Priority';
import { cleanWhiteSpace } from './utils-mini';
import { IWorkPeriod } from './IWorkPeriod';

export { Side };

export enum ActualPhase {
	planned = 'planned',
	plannedPreview = 'planned-preview',
	started = 'started',
	actualizing = 'actualizing',
	actualized = 'actualized',
	multiple = 'multiple',
}

// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7317
//TODO move
export enum TicketScale {
	Hourly = 'hourly',
	Daily = 'daily',
}
// END PLACEHOLDER REPLACE AS PART OF TP-7317

//TODO - centralize and unify with the other points
interface IPoint {
	x: number;
	y: number;
}

interface IPrevData {
	roleId?: string;
	locationId?: string;
	priorityId?: string;
	requestedByProjectMemberId?: string;
	requestedByProjectMemberRoleId?: string;
	assignedProjectMemberId?: string;
	assignedProjectMemberRoleId?: string;
	responsibleProjectMemberId?: string;
	responsibleProjectMemberRoleId?: string;
	ticketExceptionDays?: string; //this one will actually be an id in this list
}

export const defaultPullTicketDuplicateOffset: IPoint = { x: 0, y: 145 };
export const defaultActiveTicketDuplicateOffset: IPoint = { x: 0, y: 70 };

const SHORT_ACTIVITY_NAME_LENGTH: number = 500;
const CONSTRAINT_ACTIVE_SIZE: number = 145;
const CONSTRAINT_PULL_SIZE: number = 290;

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface TicketFields {
	actualPhase?: ActualPhase;
	previousActualPhase?: ActualPhase;
	actualPhaseOverride?: ActualPhase;
	activityName?: string;
	actualCrewSize?: number;
	actualDurationDays?: number;
	actualDurationDaysInferred?: number;
	actualDurationHours?: string;
	actualFinish?: string;
	actualStart?: string;
	actualStartInferred?: string;
	actualWorkingDays?: boolean[];
	actualWorkingDaysInferred?: boolean[];
	actualWorkingPeriods?: IWorkPeriod[];
	assignedProjectMemberId?: string;
	assignedProjectMember?: ProjectMember;
	assignedProjectMemberRoleId?: string;
	assignedProjectMemberRole?: Role;
	createdAt?: string;
	creatorId?: string;
	crewSize?: number;
	constraintState?: CONSTRAINT_ACCEPTANCE_STATE;
	dateRequested?: string;
	dateRequired?: string;
	durationDays?: number;
	durationHours?: string;
	hasPredecessorOutOfOrder?: boolean;
	hasScheduleViolations?: boolean;
	height?: number;
	id?: string;

	isAccepted?: boolean;
	isActive?: boolean;
	isActualized?: boolean; //local
	isBlocked?: boolean;
	isCyclic?: boolean;
	isLateConstraintRequested?: boolean;
	isLateTicketFinish?: boolean;
	isOutOfOrder?: boolean;
	isPlanArchived?: boolean;
	lastPlannedCrewSize?: number;
	lastPlannedDurationDays?: number;
	lastPlannedDurationHours?: string;
	lastPlannedFinish?: string;
	lastPlannedStart?: string;
	lastPlannedWorkingDays?: boolean[];
	lastPlannedWorkingPeriods?: IWorkPeriod[];
	left?: number;
	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7317
	leftGranular?: number;
	leftDay?: number;
	// END PLACEHOLDER REPLACE AS PART OF TP-7317
	legacyDatePlanned?: string;
	legacyDateRequested?: string;
	liveCrewSize?: number;
	liveDurationDays?: number;
	liveDurationHours?: string;
	livePromisePeriodStack?: pinUtils.PromisePin[];
	liveLastPromise?: pinUtils.PromisePin;
	liveExceptionDays?: Map<string, boolean>;
	liveFinishX?: number;
	liveFinish?: string;
	liveRole?: Role;
	liveStart?: string;
	liveWorkingDays?: boolean[];
	liveWorkingPeriods?: IWorkPeriod[];
	liveAssignedProjectMemberId?: string; // Used to always store the current assigned project member for new edit
	needsVariance?: boolean;
	notes?: string;
	location?: Location;
	locationId?: string;
	milestoneType?: MilestoneType;
	pauseVarianceRemoval?: boolean;
	plannedFinish?: string;
	plannedStart?: string;
	predecessorOutOfOrderCount?: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	predecessors?: any; //todo, do better
	preservedTicketId?: string;
	previousAssignedProjectMembers?: IPreviousAssignedProjectMember[];
	priorityId?: string;
	priority?: Priority;
	procoreRfiId?: string;
	promisePeriodPosition?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	promisePeriodStack?: any; //todo, do better
	pullColumn?: number;
	responsibleProjectMember?: ProjectMember;
	responsibleProjectMemberId?: string;
	responsibleProjectMemberRoleId?: string;
	requestedByProjectMemberId?: string;
	requestedByProjectMember?: ProjectMember;
	requestedByProjectMemberRoleId?: string;
	requestedByProjectMemberRole?: Role;
	roleId?: string;
	/** derived from dates representation of side */
	side?: Side;
	/** derived left position */
	startTimeOffset?: string;
	/** records an intent to set a side, may not align with dates (api use mostly)  */
	ticketPosition?: Side;
	tminusFinish?: number;
	ticketExceptionDays?: Map<string, boolean>;
	// TP-626 temp workaround
	ticketExceptionDaysPrevPlan?: Map<string, boolean>;
	top?: number;
	type?: string;
	userOrder?: number;
	width?: number;
	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7317
	widthGranular?: number;
	widthDay?: number;
	// END PLACEHOLDER REPLACE AS PART OF TP-7317
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	workingDays?: any; //todo, do better
	workingPeriods?: IWorkPeriod[];

	externalScheduleId?: string;
	externalScheduleFinishDate?: string;

	wbsCode?: string;
	wbsExternalSystemId?: string;
	wbsImportId?: string;

	overwriteTransparency?: number;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
}

export enum TicketRenderTypes {
	database = 'database',
	rawDatabase = 'rawDatabase',
	drag = 'drag',
	edit = 'edit',
	locationsEdit = 'locationsEdit',
}

/**
 * # API feels stable now!
 *
 * ## General lifecycle
 * A ticket is constructed, generally using the data from a firebase child_added event.
 * It defaults to the database view. In this view the ticket is not generally
 * responding to continuous user interaction. There will be "instant" changes like
 * being selected, but the data the ticket is rendered can be safely considered to
 * be in sync with firebase. Changes will be applied via [[firebaseUpdate]],
 * additionally any derived data expected to always be kept up to date should be
 * initialized and applied here (likely additionally in [[localUpdate if it changes locally]])
 *
 * To change to a longer term/ ignoring database mode (drag/edit/new thing), a
 * ticket should be converted to that view via [[changeToSecondaryView]]. Where view
 * specific (and general) database -> edit/drag/other conversions are performed.
 * Any time a ticket needs to be changed, the relevant updates should be applied to
 * [[localUpdate]]. Once the long term view is done, the code that required it should
 * call [[prepareDataForSave]] and exit the view [[changeToDatabaseView]].
 *
 * ## Actual usage
 * Most of the app should never call any write methods, and never modify anything on the Ticket.
 * All changes to data are expected to run through the action system.
 * There may be some small exceptions to that rule though. Additional notes:
 * * never retain a reference to the .view object, it's only a reference, and that
 * reference will change, frequently
 * * the "real" views (database, edit, drag) will always refer to the same object
 * throughout that view "session". This means that you'll be able to store some
 * data in the view while your using it, it will be otherwise ignored and cleared the
 * next time that view is initialized (don't rely on it being there at the start
 * of the next view session though, consider it cleared at the end of the view session).
 * Probably don't rely on that, but the option is there.
 *
 * ## Derived data
 *
 * ### actualized stuff
 * All autogenerated via a config list
 *
 * ### joined data
 * * Largely handled by the [[joinKeyMapping]]
 *
 * ### special cases
 * Stuff like promise periods, [[magicRoleRecalc]] and predecessors, etc
 * are not going to be representable by a simple config list. Most of the time the
 * workflow involving them is going to be something like:
 * * convert raw database data to some more useful internal representation on [[firebaseUpdate]]'s
 * the rest of the code will use the internal representation
 * * maintain that code where necessary, probably in [[localUpdate]]s possibly just
 * in view changes
 * * before writing back to firebase, the internal representation will need to be converted
 * back the firebase-y state, then written
 */

export class Ticket {
	// eslint-disable-next-line @typescript-eslint/typedef
	public static joinKeyMapping = TicketAttributeConfig.joinKeyMapping;
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/no-explicit-any
	public static readOnlyFilter(obj: any) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const newObj: any = {};
		Object.keys(TicketAttributeConfig.actualOnly).forEach((key) => {
			const data = TicketAttributeConfig.actualOnly[key];
			if (data.clientWrite && obj[key] !== undefined) {
				newObj[key] = obj[key];
			}
			if (data.clientWrite && obj[data.lastPlannedKey] !== undefined) {
				newObj[data.lastPlannedKey] = obj[data.lastPlannedKey];
			}
		});
		//simple list
		['left', 'width', 'promisePeriodStack', 'promisePeriodPosition'].forEach((key) => {
			if (obj[key] !== undefined) {
				newObj[key] = obj[key];
			}
		});
		return newObj;
	}
	public static canAddPin(view: TicketFields): boolean {
		if (!view) {
			return false;
		}
		return (
			!view.liveLastPromise ||
			(view.liveLastPromise &&
				view.plannedFinish !== view.liveLastPromise.data.plannedFinish &&
				!view.needsVariance)
		);
	}
	public static canRemovePin(view: TicketFields): boolean {
		if (!view) {
			return false;
		}
		return !!view.liveLastPromise && view.plannedFinish === view.liveLastPromise.data.plannedFinish;
	}

	public getXFromDate!: (date: string, dayOffset?: number) => number;
	public getPullColumnXFromDate!: (date?: string) => number;
	public checkForLongestPull!: (pullColumn: number, plannedStart: string) => boolean;
	public getDefaultWorkdaysForLegacyCompletedTickets!: (
		startDate: string,
		finishDate: string,
		roleId?: string
	) => boolean[];

	/** the tickets firebase id */
	public $id: string;
	/** the data corresponding to the current state. Is a reference to the actual view */
	public view: TicketFields = {};
	/** the data corresponding to the database (unfiltered) */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public rawDatabase: any = {};
	/** the data corresponding to the database (cleaned up a bit) */
	public database: TicketFields = {};
	/** drag data */
	public drag: TicketFields = {};
	/** edit data */
	public edit: TicketFields = {};
	/** special location popup edit data */
	public locationsEdit: TicketFields = {};
	/** used to determine when joined data needs to be updated */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public prevData: IPrevData = {};
	/** the currently active renderType */
	public renderType: TicketRenderTypes = TicketRenderTypes.database;
	/** defines if the ticket is currently selected or not */
	public selected: boolean = false;
	public cycleSelected: boolean = false;
	/** defines if the ticket is currently focused */
	public focused: boolean = false;
	/** sketchy secret property added by ticketDragService to make a ticket as a "ghost" for the rendering code */
	public isGhost?: boolean;
	/** is it the longest, this is not on the view because it's got a hard dependency on fbdb and so live support is pointless */
	public isLongestPull?: boolean;
	/** specifies the weight of the dependency view this ticket is involved in (usually just means the border) */
	// dependencyWeight?: DependencyWeight;
	/** assorted bits of external data tickets need to be aware of */
	// eslint-disable-next-line @typescript-eslint/typedef
	public externalProperties = {
		ticketMovedToFloating: false,
		shouldHideCompletedTickets: false,
		granularScale: TicketScale.Daily, // should the ticket display in granular mode
	};
	/** special property used temporarily to know if a timeline entry was added in in this edit session so see if it needs to be removed on cancel */
	public wasJustAddedToTimeline?: boolean;
	/** special property used to indicate if a ticket was just added, to be removed when saving in edit mode */
	public ticketJustAdded?: boolean;

	/** the last known position of the active line */
	public activeLineX: number = null!;
	public planHasActiveSpace: boolean = false;

	public isAFloatingTicket: boolean = false;

	public isTimelineTicket: boolean = false;
	public timelineX: number | null = null;
	public timelineTicketSplitId?: [string, string];
	public varianceList?: Map<string, Variance>;

	public hidden: boolean = false;

	// isFiltered field is used to store if ticket was filtered out by the user, it is a derived field from overwriteTransparency
	// when overwriteTransparency > 0, it means ticket was filtered out by the user
	public isFiltered: boolean = false;

	/**
	 * Indicates the type of application being executed.
	 * Should be set once at the beginning of the application.
	 * Possible values: 'frontend' for Angular application, 'api' for Nest application.
	 * Do not edit this value after initialization.
	 */
	public static applicationType: 'frontend' | 'api' = 'frontend';

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public constructor(ticketData: any, key: string) {
		//temp
		this.$id = key;
		if (ticketData) {
			this.firebaseUpdate(ticketData);
		}

		this.view = this.database;
	}

	//------------------------------
	//- Helper functions
	//------------------------------

	/**
	 * Generates a very small and very specific previous value cache for the join code.
	 * Should be called on every update.
	 */
	public populatePrev(subList?: keyof IPrevData | (keyof IPrevData)[]): void {
		let supportedPrev: (keyof IPrevData)[] = [
			'roleId',
			'locationId',
			'priorityId',
			'requestedByProjectMemberId',
			'requestedByProjectMemberRoleId',
			'assignedProjectMemberId',
			'assignedProjectMemberRoleId',
			'responsibleProjectMemberId',
			'responsibleProjectMemberRoleId',
		];
		if (subList) {
			if (typeof subList === 'string') {
				if (supportedPrev.indexOf(subList) !== -1) {
					supportedPrev = [subList];
				}
			} else if (subList.every((thing) => supportedPrev.indexOf(thing) !== -1)) {
				supportedPrev = subList;
			}
		}

		supportedPrev.forEach((key) => {
			this.prevData[key] = this.view[key] as string;
		});
	}

	//-----------------------------
	//- Update code
	//-----------------------------

	/**
	 * Should be called with any updates coming specifically from firebase (or anything that wants to simulate a firebase update)
	 * Updates both rawDatabase and database
	 * @param  ticketData DataSnapshot.val()
	 */
	public firebaseUpdate(ticketData: { [x: string]: unknown }): void {
		this.rawDatabase = ticketData;
		this.populatePrev();

		const isActual = !!this.rawDatabase.actualFinish;
		const isStarted = this._isStarted(this.rawDatabase.actualStart, this.rawDatabase.actualFinish);

		for (const [key, attrData] of Object.entries(TicketAttributeConfig.baseList)) {
			const exists = ticketData[key] !== null && ticketData[key] !== undefined;

			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7317
			// - experimental temp solution
			// - load all the granular values into the existing values
			// - this is mostly meant to be commented/ uncommented in the short term
			// - because fbdb automatically fixes left values to match the columns this will... probably kind of work for writes too
			if (key === 'leftDay' || key === 'widthDay') {
				continue;
			}
			if (key === 'left') {
				firebaseUtils.addOrRemoveFirebaseProperty(this.database, 'leftDay', exists, ticketData[key]);
				// continue;
			}
			if (key === 'width') {
				firebaseUtils.addOrRemoveFirebaseProperty(this.database, 'widthDay', exists, ticketData[key]);
				// continue;
			}

			//TODO - may want to default the left/ width in here too
			// if (key === 'leftGranular') {
			// 	firebaseUtils.addOrRemoveFirebaseProperty(this.database, 'left', exists, ticketData[key]);
			// 	continue;
			// }
			// if (key === 'widthGranular') {
			// 	firebaseUtils.addOrRemoveFirebaseProperty(this.database, 'width', exists, ticketData[key]);
			// 	continue;
			// }
			// END PLACEHOLDER REPLACE AS PART OF TP-7317

			//actualized branch
			if (isActual && attrData.plannedKey) {
				firebaseUtils.addOrRemoveFirebaseProperty(
					this.database,
					attrData.localKeyOverride!,
					exists,
					ticketData[key]
				);
			}
			//planned branch
			if (!isActual && attrData.actualKey) {
				firebaseUtils.addOrRemoveFirebaseProperty(
					this.database,
					attrData.localKeyOverride!,
					exists,
					ticketData[key]
				);
			}

			//just update normally
			firebaseUtils.addOrRemoveFirebaseProperty(this.database, key, exists, ticketData[key]);
		}

		this.restoreLegacyPlannedIds();

		//derived stuff
		this.database.isActualized = isActual;
		this.setActualPhase(this.getActualPhase(isActual, isStarted));
		this.database.shortActivityName = this.database.activityName
			? cleanWhiteSpace(this.database.activityName).slice(0, SHORT_ACTIVITY_NAME_LENGTH)
			: null;
		this.database.livePlanGranularity = this.database.durationHours ? 'hour' : 'day';

		if (this.database.promisePeriodStack) {
			const stack = pinUtils.makePromisePeriodArray(
				this.database.promisePeriodStack,
				this.database.plannedFinish!,
				this.getXFromDate,
				this.varianceList
			);
			// logger.log('stack', this);
			this.database.livePromisePeriodStack = stack!;
			if (stack) {
				this.database.liveLastPromise = stack[stack.length - 1];
			}
		} else if (this.database.livePromisePeriodStack) {
			delete this.database.livePromisePeriodStack;
			delete this.database.liveLastPromise;
		}

		if (this.database.actualFinish) {
			this.database.liveFinishX = this.getXFromDate(this.database.actualFinish);
		} else if (this.database.liveFinishX) {
			delete this.database.liveFinishX;
		}
		if (
			this.database.pullColumn !== undefined &&
			this.database.pullColumn !== null &&
			!!this.database.plannedStart
		) {
			this.isLongestPull = this.checkForLongestPull(this.database.pullColumn, this.database.plannedStart);
		} else if (this.isLongestPull) {
			delete this.isLongestPull;
		}

		if (this.getPullColumnXFromDate) {
			this.timelineX = this.getPullColumnXFromDate(this.view.liveFinish);
		}

		//THIS MIGHT BE A PROBLEM
		if (this.database.type === TicketType.Constraint || this.database.type === TicketType.Milestone) {
			this.database.liveDurationDays = 1;
		}

		this.changeScale(this.externalProperties.granularScale);
		if (!this.recalcSide(TicketRenderTypes.database)) {
			//update the sizes if recalcSide doesn't
			this.updateSize(TicketRenderTypes.database);
		}

		this.recalcConstraintAcceptance(TicketRenderTypes.database);
		if (this.database.type === TicketType.Constraint) {
			this.magicRoleRecalc(TicketRenderTypes.database);
			this.magicMigrationRecalc(TicketRenderTypes.database);
		}

		this.recalcHidden();
	}

	/**
	 * this method sets the isFiltered status based on overwriteTransparency ticket field
	 */
	public updateFilteredStatus(): void {
		this.isFiltered =
			typeof this.view.overwriteTransparency === 'number' && this.view.overwriteTransparency >= 0;
	}

	/**
	 * Should be called to update the app-wide render of the ticket or the "view"
	 * Any changes here are representing changes only to the local data.
	 * The database view *can* be written to view this key, but no guarantee should be
	 * assumed that said data won't be clobbered by a real database update.
	 * Some processing of the data passed in will be done:
	 * * live data will be converted to real
	 * * any necessary derived data will be recalculated
	 * * updates to ids corresponding to joined data will trigger join updates
	 * @param  obj        The data to write
	 * @param  renderType Defaults to the current view, can override to write to another one.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public localUpdate(obj: any, renderType?: TicketRenderTypes): void {
		if (!renderType) {
			renderType = this.renderType;
		}
		const view: TicketFields = this[renderType];
		this.populatePrev();
		let isActual: boolean;

		//use the new actual value
		if (obj.actualPhaseOverride !== undefined || obj.actualFinish !== undefined) {
			if (obj.actualPhaseOverride === ActualPhase.actualizing) {
				isActual = true;
			} else if (obj.actualPhaseOverride === ActualPhase.plannedPreview) {
				isActual = false;
			} else {
				isActual = obj.actualFinish !== undefined ? !!obj.actualFinish : !!view.actualFinish;
			}
		}
		//use the old actual value
		else {
			isActual = this[renderType].isActualized as boolean;
		}
		const actualChanged = this[renderType].isActualized !== isActual;
		this[renderType].isActualized = isActual;

		// verify if actual start has changed before ticket is updated so actual phase can be recalculated
		const actualStartChanged =
			obj.actualStart !== undefined && obj.actualStart !== this[renderType].actualStart;

		//validation stuff
		if (obj.side !== undefined) {
			delete obj.side;
		} //let recalcSide do the work
		if (this[renderType].type === TicketType.Milestone || this[renderType].type === TicketType.Constraint) {
			if (obj.width) {
				delete obj.width;
			}
			if (obj.height) {
				delete obj.height;
			}
		}

		for (const [key, val] of Object.entries(obj)) {
			this[renderType][key] = val;

			// Next code is to update values consistently in the view object
			// For now this is the most stable version, but probably not the best in performance.
			// There could be some rewrites that can be improved,
			// A possible enhancement for next version: TicketAttributeConfig cache

			const liveKey = TicketAttributeConfig.reverseLiveMap[key]
				? key
				: (TicketAttributeConfig.localObjectSummary[key]?.localKeyOverride as string);
			const plannedKey = TicketAttributeConfig.plannedOnly[key]
				? key
				: TicketAttributeConfig.reverseLiveMap[liveKey]?.planned;
			const actualKey = TicketAttributeConfig.actualOnly[key]
				? key
				: TicketAttributeConfig.reverseLiveMap[liveKey]?.actual;

			if (isActual) {
				if (obj[liveKey] !== undefined) {
					if (obj[plannedKey] !== undefined && obj[actualKey] !== undefined) {
						view[liveKey] = obj[liveKey];
						view[actualKey] = obj[liveKey];
						view[plannedKey] = obj[plannedKey];
					} else if (obj[actualKey] !== undefined) {
						view[liveKey] = obj[liveKey];
						view[actualKey] = obj[liveKey];
					} else if (obj[plannedKey] !== undefined) {
						view[liveKey] = obj[liveKey];
						view[actualKey] = obj[liveKey];
						view[plannedKey] = obj[plannedKey];
					} else {
						view[actualKey] = obj[liveKey];
					}
				} else if (obj[actualKey] !== undefined) {
					view[liveKey] = obj[actualKey];
				}
			} else {
				if (obj[liveKey] !== undefined) {
					if (obj[plannedKey] !== undefined) {
						view[liveKey] = obj[plannedKey];
						view[plannedKey] = obj[plannedKey];
						view[actualKey] = obj[liveKey];
					} else {
						view[plannedKey] = obj[liveKey];
					}
				} else if (obj[plannedKey] !== undefined) {
					view[liveKey] = obj[plannedKey];
				}
			}
		}

		//special handling for promise periods (stack takes priority)
		//if stack is provided, set last to the last one
		//if last is provided, replace the last item in the array
		if (obj.livePromisePeriodStack !== undefined) {
			//auto null empty stacks
			if (
				obj.livePromisePeriodStack === null ||
				(obj.livePromisePeriodStack && obj.livePromisePeriodStack.length === 0)
			) {
				this[renderType].livePromisePeriodStack = null;
				this[renderType].liveLastPromise = null;
			}
			// if(obj.livePromisePeriodStack === null){ this[renderType].liveLastPromise = null; }
			else {
				this[renderType].liveLastPromise = obj.livePromisePeriodStack[obj.livePromisePeriodStack.length - 1];
			}
		} else if (obj.liveLastPromise !== undefined) {
			if (obj.liveLastPromise === null) {
				this[renderType].livePromisePeriodStack = null;
			} else {
				this[renderType].livePromisePeriodStack[this[renderType].livePromisePeriodStack.length - 1] =
					obj.liveLastPromise;
			}
		}

		if (obj.activityName !== undefined) {
			if (obj.activityName !== null && obj.activityName.slice) {
				this[renderType].shortActivityName = obj.activityName.slice(0, SHORT_ACTIVITY_NAME_LENGTH);
			} else {
				this[renderType].shortActivityName = null;
			}
		}

		if (obj.durationHours !== undefined || obj.durationDays !== undefined) {
			if (obj.durationDays) {
				this[renderType].livePlanGranularity = 'day';
			}
			if (obj.durationHours) {
				this[renderType].livePlanGranularity = 'hour';
			}
		}

		//might make more sense to change this to not use the override?
		if (obj.actualPhaseOverride !== undefined || obj.actualFinish !== undefined || actualStartChanged) {
			this.setActualPhase(
				this.getActualPhase(
					!!this[renderType].actualFinish,
					this._isStarted(this[renderType].actualStart, this[renderType].actualFinish),
					// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6179
					// - maybe use the view version as an or, also need to figure out when to clear the override
					obj.actualPhaseOverride
					// END PLACEHOLDER REPLACE AS PART OF TP-6179
				),
				renderType
			);
		}

		//promise period stuff - run every time at first, can probably filter it to only change on interesting updates
		if (
			this[renderType].liveLastPromise &&
			this[renderType].liveLastPromise.data.actualFinish !== this[renderType].actualFinish
		) {
			this[renderType].liveLastPromise.data.actualFinish = this[renderType].actualFinish;
		}

		//update live
		if (actualChanged) {
			this.switchLiveKeys(isActual);

			//recalc left/ width
			// skip if the values it needs aren't defined and the values weren't specifically set
			if (obj.left === undefined && view.liveStart) {
				view.left = this.getXFromDate(view.liveStart, 1);
			}
			if (obj.width === undefined && view.liveWorkingDays) {
				view.width = view.liveWorkingDays.length * PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH;
			}
		}

		if (obj.left !== undefined || (obj.left === undefined && actualChanged)) {
			this.recalcSide(renderType);
		}

		this.recalcPins(renderType);
		this.recalcConstraintAcceptance(renderType);

		//enhancement, only do this on finish changing
		const finish =
			obj.actualFinish || (isActual && this[renderType].actualFinish) ? this[renderType].actualFinish : null;
		if (finish) {
			this[renderType].liveFinishX = this.getXFromDate(finish);
		}

		const overwriteTransparency = (obj as Partial<TicketFields>)?.overwriteTransparency;

		// Update the transparency value only if the render type is not 'database'.
		// This ensures the 'database' transparency setting stays synchronized with the render configuration
		if (this.renderType !== TicketRenderTypes.database) {
			this.database.overwriteTransparency = overwriteTransparency;
		}
		// Update the 'isFiltered' status based on the new transparency value.
		this.updateFilteredStatus();

		//type specific processing
		switch (renderType) {
			case TicketRenderTypes.drag:
				this.dragUpdate(obj);
				break;
			case TicketRenderTypes.edit:
				this.editUpdate(obj);
				break;
			default:
				break;
		}
	}

	/** Special variant of [[localUpdate]] that replaces all data instead of merging it in */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public overwriteLocalView(obj: { [x: string]: any }, renderType?: TicketRenderTypes): void {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const newObj: { [key: string]: any } = {};
		Object.keys(TicketAttributeConfig.localObjectSummary).forEach((key) => {
			newObj[key] = obj[key] !== undefined ? obj[key] : null;
		});
		//exclude live keys, so that the "real" values are used
		Object.keys(TicketAttributeConfig.reverseLiveMap).forEach((key) => delete newObj[key]);
		this.localUpdate(newObj, renderType);
	}

	//placeholders to allow view specific local update overrides
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public dragUpdate(obj: any): void {}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public editUpdate(obj: any): void {}

	//-----------------------------
	//- Changing views code
	//-----------------------------

	/**
	 * Change back to the database view, this can be considered as throwing out any un-persisted changes.
	 * If for some reason reason we "need" the ability to load secondary view into database
	 * without persisting it, it should be carefully considered first, it's likely a mistake.
	 * Then the output functions could _probably_ be called here.
	 */
	public changeToDatabaseView(): void {
		this.renderType = TicketRenderTypes.database;
		this.view = this[this.renderType];
		this.hidden = false;
	}

	/**
	 * Change the ticket to one of it's secondary views, part of this process involves
	 * initializing the view with a set of database data
	 * @param  renderType
	 */
	public changeToSecondaryView(renderType: TicketRenderTypes, forceRecalc: boolean = false): void {
		if (!forceRecalc && renderType === this.renderType) {
			logger.log("don't change the renderType to be the same");
			return;
		}

		switch (renderType) {
			case TicketRenderTypes.drag:
				this.viewInputDrag();
				break;
			case TicketRenderTypes.edit:
				this.viewInputEdit();
				break;
			case TicketRenderTypes.locationsEdit:
				this.viewInputLocationsEdit();
				break;
			default:
				break;
		}

		//set the view last
		this.renderType = renderType;
		this.view = this[this.renderType];
	}

	/** Called at the beginning of the drag */
	// eslint-disable-next-line @typescript-eslint/naming-convention
	private viewInputDrag(): void {
		this.viewInputGeneric(TicketRenderTypes.drag);
	}
	/** Called at the beginning of the edit */
	// eslint-disable-next-line @typescript-eslint/naming-convention
	private viewInputEdit(): void {
		this.viewInputGeneric(TicketRenderTypes.edit);
		this.setActualPhase(
			this.getActualPhase(
				!!this.view.actualFinish,
				this._isStarted(this.view.actualStart, this.view.actualFinish)
			)
		);
		this.magicRoleRecalc(TicketRenderTypes.edit);
	}
	/** Called at the beginning of the locations edit */
	// eslint-disable-next-line @typescript-eslint/naming-convention
	private viewInputLocationsEdit(): void {
		this.viewInputGeneric(TicketRenderTypes.locationsEdit);
	}

	//temp
	// eslint-disable-next-line @typescript-eslint/naming-convention
	private viewInputGeneric(renderType: TicketRenderTypes): void {
		this[renderType] = {};
		this.view = this[renderType];

		//new version, simplifed localList that includes everything
		TicketAttributeConfig.localList.forEach((attrKey) => {
			if (this.database[attrKey] !== undefined) {
				this[renderType][attrKey] = this.database[attrKey];

				//special behavior, copy by value - TODO, need to come up with a more generic version of this
				// The issue that this is working around is that an array is being copied by reference, which is not preserving the original version
				const arrayFields = [
					'liveWorkingDays',
					'workingDays',
					'actualWorkingDays',
					'liveWorkingPeriods',
					'workingPeriods',
					'actualWorkingPeriods',
				];
				if (arrayFields.includes(attrKey)) {
					if (this.database[attrKey]) {
						this[renderType][attrKey] = [...this.database[attrKey]];
					}
				}
			}
		});

		//bypass the localList (come up with better solution
		if (this.database.plannedFinish) {
			this[renderType].plannedFinish = this.database.plannedFinish;
		}

		//magic local datas
		if (this.database.liveRole) {
			this[renderType].liveRole = this.database.liveRole;
		}
		if (this.database.livePromisePeriodStack) {
			this[renderType].livePromisePeriodStack = pinUtils.cloneStack(this.database.livePromisePeriodStack);
		}
		if (this.database.liveLastPromise) {
			this[renderType].liveLastPromise =
				this[renderType].livePromisePeriodStack[this[renderType].livePromisePeriodStack.length - 1];
		}
		this[renderType].side = this.database.side;
	}

	//-----------------------------
	//- Saving data code
	//-----------------------------

	/**
	 * Take a view, copies all it's relevant data to the database view, and
	 * returns an update object that can be used to write the changes to firebase.
	 * TODO - add support for writing to firebase from the database view
	 * @param  renderType
	 * @return           	An object to be written to firebase
	 */
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	public prepareDataForSave(renderType?: TicketRenderTypes) {
		if (!renderType) {
			renderType = this.renderType;
		}

		let out;

		switch (renderType) {
			case TicketRenderTypes.database:
				out = this.viewOutputGeneric(TicketRenderTypes.database);
				break;
			case TicketRenderTypes.drag:
				out = this.viewOutputDrag();
				break;
			case TicketRenderTypes.edit:
				out = this.viewOutputEdit();
				break;
			case TicketRenderTypes.locationsEdit:
				out = this.viewOutputGeneric(TicketRenderTypes.locationsEdit);
				break;
			default:
				break;
		}
		return out;
	}

	/**
	 * Perform any operations necessary to convert the drag data back into database data.
	 */
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/naming-convention
	private viewOutputDrag() {
		return this.viewOutputGeneric(TicketRenderTypes.drag);
	}
	/**
	 * Perform any operations necessary to convert the edit data back into database data.
	 */
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/naming-convention
	private viewOutputEdit() {
		const out = this.viewOutputGeneric(TicketRenderTypes.edit);
		delete this.wasJustAddedToTimeline; //temp - delete this key when saving
		return out;
	}

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/naming-convention
	private viewOutputGeneric(renderType: TicketRenderTypes) {
		// this.resetTempActuals(renderType);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const out: any = {};

		// eslint-disable-next-line guard-for-in
		for (const key in TicketAttributeConfig.baseList) {
			const thing = TicketAttributeConfig.baseList[key];
			if (this[renderType][key] !== undefined) {
				if (thing.clientWrite || (thing.apiWrite && Ticket.applicationType === 'api')) {
					out[key] = this[renderType][key];
				}
				if (!thing.fbdbOwned) {
					this.database[key] = this[renderType][key];
				}
			}

			//copy joinned data that may have been modified
			const derivedKey = Ticket.joinKeyMapping[key];
			if (derivedKey && this[renderType][key] !== undefined) {
				this.database[derivedKey.key] = this[renderType][derivedKey.key];
			}
		}
		//also copy the live data back
		for (const key in TicketAttributeConfig.reverseLiveMap) {
			if (this[renderType][key] !== undefined) {
				this.database[key] = this[renderType][key];
			}
		}

		//essentailly cloned from the view input... (this whole thing needs some work)
		this.database.livePromisePeriodStack = this[renderType].livePromisePeriodStack
			? pinUtils.cloneStack(this[renderType].livePromisePeriodStack)
			: null!;
		this.database.liveLastPromise = this.database.livePromisePeriodStack
			? this.database.livePromisePeriodStack[this.database.livePromisePeriodStack.length - 1]
			: null!;

		const outStack = pinUtils.returnToObject(this[renderType].livePromisePeriodStack);
		const outPos = outStack ? this[renderType].liveLastPromise.$id : null;
		this[renderType].promisePeriodStack = outStack;
		this[renderType].promisePeriodPosition = outPos;
		out.promisePeriodStack = outStack;
		out.promisePeriodPosition = outPos;
		this.magicRoleRecalc(TicketRenderTypes.database);
		this.recalcSide(TicketRenderTypes.database);

		//simple isActualized recalc
		this.database.isActualized = !!this.database.actualFinish;

		this._applyPreWriteConversions(out);

		return out;
	}

	/** runs some inplace modifications to the data right before it's written to firebase */
	private _applyPreWriteConversions<T extends { type: TicketType | null }>(ticketDataForFirebase: T): void {
		if (!ticketDataForFirebase) return;
		if (ticketDataForFirebase.type === TicketType.Task) {
			ticketDataForFirebase.type = null;
		}
	}

	/**
	 * explictly update all the actulizable live values to the passed in state
	 * @param  toActual   true = actual, false = planned
	 * @param  renderType [description]
	 * @return            [description]
	 */
	// eslint-disable-next-line @typescript-eslint/naming-convention
	private switchLiveKeys(toActual: boolean, renderType?: TicketRenderTypes): void {
		if (!renderType) {
			renderType = this.renderType;
		}

		// eslint-disable-next-line guard-for-in
		for (const key in TicketAttributeConfig.reverseLiveMap) {
			const map = TicketAttributeConfig.reverseLiveMap[key];
			this[renderType][key] = toActual ? this[renderType][map.actual] : this[renderType][map.planned];
		}
	}

	//-----------------------------
	//- "special" derived data code
	//-----------------------------

	/**
	 * Calculates a liveRole key that works for both normal tickets and constraints
	 * @param  viewType
	 */
	public magicRoleRecalc(viewType?: TicketRenderTypes): void {
		if (!viewType) {
			viewType = this.renderType;
		}
		const view: TicketFields = this[viewType];

		let role = null;
		if (view.type === 'constraint') {
			if (
				view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ACCEPTED ||
				view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.COMPLETE
			) {
				role = view.responsibleProjectMemberRole;
			} else if (
				view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ASSIGNED &&
				view.responsibleProjectMemberRole
			) {
				role = view.responsibleProjectMemberRole;
			} else if (
				view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ASSIGNED ||
				view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.UNASSIGNED
			) {
				role = view.requestedByProjectMemberRole;
			} else {
				role = null;
			}
		} else {
			if (view.role) {
				role = view.role;
			}
		}

		view.liveRole = role;
	}

	// MOC-3206 & MOC-3260
	// Provides on-the-fly migration between release-36.5 and release-37
	public magicMigrationRecalc(viewType?: TicketRenderTypes): void {
		if (!viewType) {
			viewType = this.renderType;
		}
		const view: TicketFields = this[viewType];

		if (view.type === TicketType.Constraint) {
			// In any state, if requestedByProjectMemberId is null then assign it the value of creatorId.
			if (!view.requestedByProjectMemberId) {
				view.requestedByProjectMemberId = view.creatorId || null!;
			}

			// In any state, if requestedByProjectMemberRoleId is null then assign it requestedByProjectMemberId's primary role id.
			if (
				!view.requestedByProjectMemberRoleId &&
				view.requestedByProjectMemberId &&
				view.requestedByProjectMember
			) {
				view.requestedByProjectMemberRoleId = view.requestedByProjectMember.view.roleId || null!;
			}

			// If state is Assigned and assignedProjectMemberRoleId is null, then assign it assignedProjectMemberId's primary role id.
			if (view.assignedProjectMemberId && !view.assignedProjectMemberRoleId && view.assignedProjectMember) {
				view.assignedProjectMemberRoleId = view.assignedProjectMember.view.roleId || null!;
			}

			if (
				view.responsibleProjectMemberId &&
				view.responsibleProjectMember &&
				!view.responsibleProjectMemberRoleId
			) {
				view.responsibleProjectMemberRoleId = view.responsibleProjectMember.view.roleId || null!;
			}

			// Set the liveAssignedProjectMemberId key (This is used to control assigned project member in New Edit)
			// We map the liveAssignedProjectMemberId to different properties, dependning on the constraintState.
			// That way, the "Assigned To" field in new edit will also be populated
			if (
				view.liveAssignedProjectMemberId &&
				view.assignedProjectMemberId &&
				view.liveAssignedProjectMemberId !== view.assignedProjectMemberId &&
				view.constraintState !== CONSTRAINT_ACCEPTANCE_STATE.ACCEPTED
			) {
				// Set the liveAssignedProjectMemberId to equal the assignedProjectMemberId
				view.liveAssignedProjectMemberId = view.assignedProjectMemberId;
			} else if (view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ASSIGNED) {
				// Constraint is assigned. Set live = assigned so the correct PM appears in the new edit list
				view.liveAssignedProjectMemberId = view.assignedProjectMemberId;
			} else if (view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ACCEPTED) {
				// Constraint is accepted. Set live = responsible so the correct PM appears in the Assigned To list (assignedProjectMemberId should be null in this case)
				view.liveAssignedProjectMemberId = view.responsibleProjectMemberId;
			} else if (view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.COMPLETE) {
				view.liveAssignedProjectMemberId = view.responsibleProjectMemberId;
			} else {
				// Just give up
				view.liveAssignedProjectMemberId = null!;
			}
		}
	}

	/**
	 * called by the joiner on ticketList to update the liveExceptionDays whenever the ticketExceptionDays changes
	 * note liveExceptionDays is only applied to the database view to maintain standard
	 * "database updates should not clobber edit data" behavior
	 */
	public magicExceptionDaysRecalc(): void {
		// the joiner code in ticketList doesn't _really_ do the right thing for this type of data
		// this ensures that's the database reprensentation is always correct
		if (this.renderType !== TicketRenderTypes.database && this.view.ticketExceptionDays) {
			this.database.ticketExceptionDays = new Map(this.view.ticketExceptionDays);
		}
		this.database.liveExceptionDays = this.view.ticketExceptionDays
			? new Map(this.view.ticketExceptionDays)
			: undefined;
		// completely ignore base updates for this field
		this.prevData.ticketExceptionDays = this.$id;
	}

	public recalcSide(viewType?: TicketRenderTypes): boolean {
		if (!viewType) {
			viewType = this.renderType;
		}
		let newSide;
		if (!this.planHasActiveSpace) {
			newSide = Side.PULL;
		} else if (this.activeLineX === undefined || this.activeLineX === null) {
			newSide = Side.UNKNOWN;
		} else if (this[viewType].left >= this.activeLineX) {
			newSide = Side.PULL;
		} else {
			newSide = Side.ACTIVE;
		}
		if (newSide !== this[viewType].side) {
			this[viewType].side = newSide;
			this.updateSize(viewType);
			return true;
		}
		return false;
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	private updateSize(viewType?: TicketRenderTypes): void {
		if (!viewType) {
			viewType = this.renderType;
		}
		if (this[viewType].type === 'constraint' || this[viewType].type === 'milestone') {
			if (this[viewType].side === Side.PULL) {
				this[viewType].width = this[viewType].height = CONSTRAINT_PULL_SIZE;
			} else {
				this[viewType].width = this[viewType].height = CONSTRAINT_ACTIVE_SIZE;
			}
		}
	}

	public updateVariance(varianceList: Map<string, Variance>): boolean {
		this.varianceList = varianceList;
		if (this.view.liveLastPromise) {
			this.recalcPins(this.renderType);
			return true;
		}
		return false;
	}

	//maybe pass in the pin data? otherwise this "has" to be run after recalcPins
	public recalcNeedsVariance(viewType?: TicketRenderTypes): boolean {
		if (!viewType) {
			viewType = this.renderType;
		}
		const view: TicketFields = this[viewType];
		let changed = false;

		if (
			!view.needsVariance &&
			view.actualPhase !== ActualPhase.plannedPreview && //don't need it when previewing the old version //MOC-4135
			view.liveLastPromise &&
			!view.liveLastPromise.data.varianceId &&
			view.liveLastPromise.data.plannedFinish !== view.liveFinish
		) {
			//unsure about live finish
			changed = true;
			view.needsVariance = true;
			logger.log('variance needed');
		}

		if (
			view.needsVariance &&
			((view.liveLastPromise && view.liveLastPromise.data.varianceId) ||
				(view.liveLastPromise && view.liveLastPromise.data.plannedFinish === view.liveFinish))
		) {
			changed = true;
			view.needsVariance = false;
			logger.log('variance not needed');
		}

		return changed;
	}

	public recalcHidden(): boolean {
		let desiredHiddenState = false;
		if (
			this.externalProperties.shouldHideCompletedTickets &&
			this.database.actualFinish &&
			Math.abs(dateCache.diffDays(dateCache.todayString, this.database.actualFinish)) >= 30
		) {
			desiredHiddenState = true;
		}
		if (this.externalProperties.ticketMovedToFloating) {
			desiredHiddenState = true;
		}

		if (this.hidden !== desiredHiddenState) {
			this.hidden = desiredHiddenState;
			return true;
		}
		return false;
	}

	public recalcPins(renderType?: TicketRenderTypes): boolean {
		if (!renderType) {
			renderType = this.renderType;
		}
		const view: TicketFields = this[renderType];
		const stack = this[renderType].livePromisePeriodStack;
		let changed = false;
		if (stack) {
			//this chunk removes the variance in local data
			if (
				view.liveLastPromise?.data.varianceId &&
				//MOC-4135 - is being removed when doing a lot of things in one session
				view.liveLastPromise.data.plannedFinish === view.liveFinish &&
				view.actualPhase !== ActualPhase.plannedPreview && //don't remove it when previewing the old version
				!view.pauseVarianceRemoval
			) {
				logger.log('remove variance');
				// Logging.notice('remove variance for '+view.activityName);
				delete view.liveLastPromise.data.varianceId;
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			stack.forEach((pin: pinUtils.PromisePin, idx: number, arr: any[]) => {
				const nextPin = idx < arr.length - 1 ? arr[idx + 1].data : null;
				// logger.log('renderType', renderType, this[renderType], this[renderType].plannedFinish)
				changed =
					pinUtils.decoratePinData(
						pin.data,
						this[renderType!].plannedFinish,
						this.getXFromDate,
						nextPin,
						this.varianceList
					) || changed;
			});
		}

		//possibly break this out into it's own update method?
		const finish = this[renderType].actualFinish;
		const oldLiveFinishX = this[renderType].liveFinishX;
		if (finish) {
			this[renderType].liveFinishX = this.getXFromDate(finish);
			changed = this[renderType].liveFinishX !== oldLiveFinishX || changed;
		} else if (oldLiveFinishX) {
			this[renderType].actualFinish = null;
			changed = true;
		}

		//DEVTEXT - check if the if should be there or not
		// if(changed){
		changed = this.recalcNeedsVariance(renderType) || changed;
		// }

		return changed;
	}

	public recalcConstraintAcceptance(renderType?: TicketRenderTypes): void {
		if (!renderType) {
			renderType = this.renderType;
		}
		const view: TicketFields = this[renderType];

		if (view.isActualized) {
			view.constraintState = CONSTRAINT_ACCEPTANCE_STATE.COMPLETE;
		} else if (view.assignedProjectMemberId) {
			view.constraintState = CONSTRAINT_ACCEPTANCE_STATE.ASSIGNED;
		} else if (!view.assignedProjectMemberId && view.responsibleProjectMemberId) {
			view.constraintState = CONSTRAINT_ACCEPTANCE_STATE.ACCEPTED;
		} else if (!view.assignedProjectMemberId && !view.responsibleProjectMemberId) {
			view.constraintState = CONSTRAINT_ACCEPTANCE_STATE.UNASSIGNED;
		} else if (view.requestedByProjectMemberId) {
			view.constraintState = CONSTRAINT_ACCEPTANCE_STATE.UNASSIGNED;
			// view.responsibleProjectMemberId = view.requestedByProjectMemberId;
		} else {
			view.constraintState = CONSTRAINT_ACCEPTANCE_STATE.INVALID;
		}
	}

	public changeScale(scale: TicketScale): void {
		//undecided if scale should exist on a ticket, could be worth a "check if same" thing if nothing else
		//but that's already on ticket list
		if (scale === TicketScale.Daily) {
			this.view.left = this.view.leftDay;
			this.view.width = this.view.widthDay;
			// might also copy the db version
		} else if (scale === TicketScale.Hourly) {
			this.view.left = this.view.leftGranular;
			this.view.width = this.view.widthGranular;
		}
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7317
		// - a warning system if this... "prototype" fails effectively
		if (!this.view.left || !this.view.width) {
			logger.warn('TicketView.changeScale broken values', this.view);
		}
		// END PLACEHOLDER REPLACE AS PART OF TP-7317
	}

	/**
	 * finds legacy actualized tickets and restores some data to keep them stable
	 */
	// eslint-disable-next-line @typescript-eslint/naming-convention
	private restoreLegacyPlannedIds(renderType?: TicketRenderTypes): void {
		if (!renderType) {
			renderType = this.renderType;
		}
		const view: TicketFields = this[renderType];

		//TODO - include type checks for constraints and milestones
		const isTaskTicket = view.type !== TicketType.Constraint && view.type !== TicketType.Milestone;

		if (view.actualFinish) {
			if (view.actualCrewSize === undefined && isTaskTicket) {
				view.actualCrewSize = view.crewSize;
				view.lastPlannedCrewSize = view.crewSize;
				view.liveCrewSize = view.crewSize;
			}
			if (view.actualDurationDays === undefined && isTaskTicket) {
				const actualActualDuration = view.actualDurationDaysInferred || view.durationDays;
				view.actualDurationDays = actualActualDuration;
				view.lastPlannedDurationDays = view.durationDays;
				view.liveDurationDays = actualActualDuration;
			}

			if (!view.plannedStart) {
				view.plannedStart = view.lastPlannedStart;
			}
			if (!view.plannedFinish) {
				view.plannedFinish = view.lastPlannedFinish;
			}
			if (!view.actualStart) {
				view.actualStart =
					view.actualStartInferred ||
					dateCache.addDays(view.actualFinish, -view.width! / PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH + 1);
				view.liveStart = view.actualStart;
			}
			if (!view.actualWorkingDays) {
				view.actualWorkingDays =
					view.actualWorkingDaysInferred ||
					this.getDefaultWorkdaysForLegacyCompletedTickets(view.actualStart, view.actualFinish, view.roleId);
				if (!view.workingDays) {
					view.workingDays = view.lastPlannedWorkingDays || view.actualWorkingDays;
				}
				view.liveWorkingDays = view.actualWorkingDays;
				if (!view.lastPlannedWorkingDays) {
					view.lastPlannedWorkingDays = view.workingDays;
				}
			}
		}
	}

	//to ensure the previous is set
	// eslint-disable-next-line @typescript-eslint/naming-convention
	private setActualPhase(newPhase: ActualPhase, renderType?: TicketRenderTypes): void {
		if (!renderType) {
			renderType = this.renderType;
		}
		this[renderType].previousActualPhase = this[renderType].actualPhase;
		this[renderType].actualPhase = newPhase;
	}

	//TODO - add custom subview support
	// eslint-disable-next-line @typescript-eslint/naming-convention
	private getActualPhase(
		actualFinishTruthy?: boolean,
		isStarted?: boolean,
		override?: ActualPhase
	): ActualPhase {
		if (override) {
			return override;
		}
		if (isStarted) {
			return ActualPhase.started;
		}
		return actualFinishTruthy ? ActualPhase.actualized : ActualPhase.planned;
	}

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	public convertPlannedToActual() {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const newData: any = {};

		Object.keys(TicketAttributeConfig.plannedOnly).forEach((plannedKey) => {
			const plannedKeyData = TicketAttributeConfig.plannedOnly[plannedKey];

			newData[plannedKeyData.lastPlannedKey] = this.view[plannedKey];
			newData[plannedKeyData.actualKey] = this.view[plannedKey];
		});
		if (this.view.liveLastPromise) {
			const newPin = pinUtils.clonePin(this.view.liveLastPromise);
			newPin.data.actualFinish = newData.actualFinish;
			newData.liveLastPromise = newPin;
		}
		// MOC-4354 - Special override to prevent constraint from displaying broken data when completed before accepted
		if (this.view.assignedProjectMemberId) {
			newData.responsibleProjectMemberId = this.view.assignedProjectMemberId;
			newData.responsibleProjectMemberRoleId = this.view.assignedProjectMemberRoleId;
			newData.assignedProjectMemberId = null;
			newData.assignedProjectMemberRoleId = null;
		}
		return newData;
	}

	private _isStarted(actualStart?: string, actualFinish?: string): boolean {
		return !!actualStart && !actualFinish;
	}

	/**
	 * Converts a planned ticket to a started ticket
	 * @returns the data to be written to the database
	 */
	public convertPlannedToStarted(): Partial<TicketFields> {
		return {
			actualStart: this.view.plannedStart,
		};
	}

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	public convertConstraintToAccepted() {
		return {
			assignedProjectMemberId: null,
			assignedProjectMemberRoleId: null,
			responsibleProjectMemberId: this.view.assignedProjectMemberId,
			responsibleProjectMemberRoleId: this.view.assignedProjectMemberRoleId,
		};
	}

	/** Does all the various date math and such to figure out the rightmost position the ticket "could" represent.
	 * Little consideration is given to performance here in the short term. Since this most likely want's to be some sort of cache driven
	 * by a single call to this function...
	 */
	public getTheoreticalRightEdge(): number {
		//unsure about 'dateRequested', 'dateRequired'
		const dateBasedRight = ['actualFinish', 'lastPlannedFinish', 'plannedFinish'].reduce((acc, key) => {
			if (!this.view[key]) {
				return acc;
			}
			const num = this.getXFromDate(this.view[key]);
			if (acc === undefined) {
				return num;
			}
			return Math.max(acc, num);
		}, -Infinity);
		const positionBasedRight = this.view.left! + this.view.width!;

		const promiseBasedRight = this.view.livePromisePeriodStack
			? this.view.livePromisePeriodStack.reduce((acc, pin) => {
					return Math.max(acc, pin.data.finishX || -Infinity);
			  }, -Infinity)
			: -Infinity;

		return Math.max(dateBasedRight, positionBasedRight, promiseBasedRight);
	}
}
